import {useEffect, useLayoutEffect, useRef, useState, useCallback} from "react";
import "./prechat.css";
import { prechatUtil } from "../helpers/prechatUtil";
import {DEPLOYMENT_CONFIGURATION_CONSTANTS, REQUIRED_FIELDS} from "../helpers/constants";
import { useStrings } from '../i18n/useStrings';
import { AiOutlineLoading } from 'react-icons/ai';

/**
 * Maps technical field names to translation keys
 * Only for fields that are actually displayed in the Prechat form
 */
function getFieldLabelKey(fieldName) {
    const mapping = {
        '_firstName': 'firstName',
        '_lastName': 'lastName',
        '_email': 'email'
    };
    return mapping[fieldName] || null;
}

export default function Prechat(props) {
    const strings = useStrings();
    const [visiblePrechatFields, setVisiblePrechatFields] = useState([]);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const formRef = useRef(null);

    useEffect(() => {
        const prechatFormFields = prechatUtil.getPrechatFormFields() || [];
        const visibleFields = prechatFormFields.filter(field => !field.isHidden);
        setVisiblePrechatFields(visibleFields);
    }, []);

    const recomputeValidity = useCallback(() => {
        const form = formRef.current;
        if (!form) return;
        setIsFormValid(form.checkValidity());
    }, []);

    // Revalidate form when default values are provided
    // Using useLayoutEffect to ensure DOM is updated before validation
    useLayoutEffect(() => {
        if (props.defaultValues && Object.keys(props.defaultValues).length > 0) {
            recomputeValidity();
        }
    }, [props.defaultValues, recomputeValidity]);

    // Using native HTML5 validation for email; no custom validator needed

    function handlePrechatFormSubmit(event) {
        event.preventDefault();

        // Prevent double submission
        if (isSubmitting) {
            return;
        }

        const form = event.currentTarget;

        // 1) Validation native HTML5
        if (!form.checkValidity()) {
            // Affiche les messages natifs du navigateur
            form.reportValidity();
            // Mettre le focus sur le premier invalide (pratique UX)
            const firstInvalid = form.querySelector(":invalid");
            if (firstInvalid?.focus) firstInvalid.focus();
            return;
        }

        // 2) Construire le payload proprement (inclut checkboxes)
        const payload = {};
        for (const field of visiblePrechatFields) {
            const el = form.elements.namedItem(field.name);
            if (!el) continue;

            // Checkbox: inclure true/false
            const fieldTypeLower = String(field.type || "").toLowerCase();
            if (fieldTypeLower === "checkbox") {
                payload[field.name] = /** @type {HTMLInputElement} */ (el).checked;
                continue;
            }

            // Valeur string (trim)
            let value = /** @type {HTMLInputElement | HTMLSelectElement} */ (el).value ?? "";

            // Email en minuscules + trim
            if (fieldTypeLower === "email") {
                value = String(value).trim().toLowerCase();
            } else {
                value = String(value).trim();
            }

            payload[field.name] = value;
        }

        // 3) Soumission avec gestion du loading state
        setIsSubmitting(true);
        
        // Handle promise-based submission if available
        const result = props.prechatSubmit(payload);
        if (result && typeof result.catch === 'function') {
            result.catch((error) => {
                console.error('Prechat submission failed:', error);
                setIsSubmitting(false);
            });
        }
    }

    return (
        <div className="messagingPrechat">
            <form
                ref={formRef}
                className="prechatForm"
                id="prechatForm"
                onSubmit={handlePrechatFormSubmit}
                onInput={recomputeValidity}
                onChange={recomputeValidity}>
                <ul className="prechatFormFieldsListView">
                    {visiblePrechatFields.map((field) => (
                        <li className="prechatFormFieldContainer" key={field.name}>
                            <FormField
                                field={field}
                                strings={strings}
                                defaultValue={props.defaultValues?.[field.name]}
                            />
                        </li>
                    ))}
                    <li className="prechatFormFieldContainer">
                        <div className="formGroup">
                            <button type="submit"
                                    className="startConversationButton"
                                    disabled={!isFormValid || isSubmitting}
                                    aria-disabled={!isFormValid || isSubmitting}>
                                {isSubmitting ? (
                                    <span className="loading-content">
                                        <AiOutlineLoading className="spinner-icon" />
                                        {strings.starting}
                                    </span>
                                ) : strings.startConversation}
                            </button>
                        </div>
                    </li>
                </ul>
            </form>
        </div>
    );
}


function FormField({ field, strings, defaultValue }) {
    const inputId = `input_${field.name}`;

    // Get translated label or fallback to field.label or field.name
    const labelKey = getFieldLabelKey(field.name);
    const label = (labelKey && strings[labelKey]) || field.label || field.name;
    const fieldTypeLower = String(field.type || "").toLowerCase();
    const isChoiceList = fieldTypeLower === String(DEPLOYMENT_CONFIGURATION_CONSTANTS.SUPPORTED_PRECHAT_FORM_FIELDS.CHOICELIST).toLowerCase();
    const isCheckbox = fieldTypeLower === String(DEPLOYMENT_CONFIGURATION_CONSTANTS.SUPPORTED_PRECHAT_FORM_FIELDS.CHECKBOX).toLowerCase();
    const isRequired = REQUIRED_FIELDS[field.name] ?? field.required;

    let inputElement = null;

    /**
     * Handle a click event from an input element to put focus on it.
     * @param {object} event
     */
    function handlePrechatFormFieldClick(event) {
        if (event && event.target.type !== "checkbox") {
            event.target.focus();
        }
    }

    if (isChoiceList && field.choiceListId) {
        const choiceList = prechatUtil.getPrechatFormChoiceList(field.choiceListId);
        const values = choiceList?.[0]?.choiceListValues || [];
        inputElement = (
            <select
                id={inputId}
                name={field.name}
                className="prechatFormField"
                required={isRequired}
                defaultValue={defaultValue || ""}
            >
                {values.map(option => (
                    <option value={option.choiceListValueName} key={option.choiceListValueId}>
                        {option.choiceListValueName}
                    </option>
                ))}
            </select>
        );
    } else {
        const isEmail = fieldTypeLower === "email";
        let inputType;

        switch (fieldTypeLower) {
            case "email":
                inputType = "email";
                break;
            case "number":
                inputType = "number";
                break;
            case "phone":
                inputType = "tel";
                break;
            case "checkbox":
                inputType = "checkbox";
                break;
            case "text":
            case "":
                inputType = "text";
                break;
            default:
                inputType = "text";
        }

        if (isCheckbox) {
            inputElement = (
                <input
                    id={inputId}
                    name={field.name}
                    type={inputType}
                    className={`prechatFormField checkbox`}
                    required={isRequired}
                    maxLength={field.maxLength}
                    onClick={handlePrechatFormFieldClick}
                    defaultChecked={Boolean(defaultValue)}
                />
            );
        } else {
            inputElement = (
                <input
                    id={inputId}
                    name={field.name}
                    type={inputType}
                    className={`prechatFormField`}
                    required={isRequired}
                    maxLength={field.maxLength}
                    onClick={handlePrechatFormFieldClick}
                    pattern={isEmail ? "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$" : undefined}
                    autoComplete={isEmail ? "email" : undefined}
                    inputMode={isEmail ? "email" : undefined}
                    defaultValue={defaultValue || ""}
                />
            );
        }
    }

    return (
        <div className="formGroup">
            <label htmlFor={inputId} className="fieldLabel">
                {label}
                {isRequired && (
                    <span className="requiredFieldIndicator" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
                          <circle cx="2" cy="2" r="2" fill="var(--accent-warning-400)" />
                        </svg>
                      </span>
                )}
            </label>
            {inputElement}
        </div>
    );
}